/**
 * 밀크런 예상비용 — 아직 입고하지 않은 상품의 개당 밀크런비를 추정한다.
 *
 * 실적 배분(server.js processMilkrunData)과는 방향이 반대다.
 *   실적: 정산 총액(totalFee)을 박스 표준가 비율로 쪼갠다  → 총액이 먼저 있다
 *   예상: 박스에 몇 개 들어가는지 세서 박스요금을 나눈다    → 총액이 아직 없다
 * 요금표(BOX_FEE)는 양쪽이 같은 값을 쓴다. 바뀌면 server.js 의 BOX_FEE_WEIGHT 도 같이 고쳐야 한다.
 *
 * 왜 기하적재만으로는 안 되나:
 *   의류·매트·쿠션처럼 눌러 담기는 것과 유리·도자기처럼 완충재가 필요한 것이
 *   같은 부피라도 실제 적재수량이 3배 넘게 차이난다. 소재를 코드가 알 수는 없어서,
 *   밀크런 실적 88,950 박스에서 카테고리별로 (실제수량 ÷ 기하수량) 을 역산해 계수로 쓴다.
 *   → milkrunFillCoef.json (표본 30건 이상 카테고리만, 나머지는 fallback)
 */
import FILL_COEF from './milkrunFillCoef.json';

// 박스 1/2/3호. 키는 쿠팡 입고 API 의 parcelBoxes[].boxSize 열거값(= server.js BOX_FEE_WEIGHT 와 동일).
// sumMm = 세 변의 합(mm) 제한, fee = 부가세 포함 표준단가.
export const BOXES = [
  { size: 'BOX_100CM', label: '1호', sumMm: 1000, fee: 2200 },
  { size: 'BOX_120CM', label: '2호', sumMm: 1200, fee: 3300 },
  { size: 'BOX_160CM', label: '3호', sumMm: 1600, fee: 4400 }
];

/**
 * 세 변의 합이 sumMm 이하인 박스에 상품을 몇 개 담을 수 있나 (변별 정수 적재).
 *
 * 부피로 나누면 안 된다 — 세 변 합 제약 때문에 긴 상품은 박스 모양이 강제된다.
 * (800mm 짜리는 1600 제한에서 800×400×400 박스밖에 못 잡는다)
 * 그래서 축마다 몇 줄씩 들어가는지를 정수로 세고, nx·x + ny·y + nz·z <= sumMm 안에서
 * nx·ny·nz 를 최대화한다. 최적해는 각 축이 sumMm/3 근처라 그 주변만 탐색하면 충분하다.
 *
 * @param {number} x 가로(mm)
 * @param {number} y 세로(mm)
 * @param {number} z 높이(mm)
 * @param {number} sumMm 세 변 합 제한(mm)
 * @returns {number} 적재 가능 수량 (담을 수 없으면 0)
 */
export function fitCount (x, y, z, sumMm) {
  if (!(x > 0 && y > 0 && z > 0 && sumMm > 0)) return 0;
  const d = [x, y, z];
  // 나머지 두 변에 최소 1mm 씩은 남아야 한다
  if (Math.max(d[0], d[1], d[2]) > sumMm - 2) return 0;
  const base = d.map(v => Math.max(1, Math.floor((sumMm / 3) / v)));
  const cand = d.map((v, i) => {
    const set = new Set();
    for (let k = Math.max(1, base[i] - 3); k <= base[i] + 6; k++) set.add(k);
    set.add(1);
    set.add(Math.max(1, Math.floor(sumMm / v)));
    return [...set].filter(k => k * v <= sumMm - 2);
  });
  let best = 0;
  for (const a of cand[0]) {
    for (const b of cand[1]) {
      for (const c of cand[2]) {
        if (a * d[0] + b * d[1] + c * d[2] > sumMm) continue;
        const n = a * b * c;
        if (n > best) best = n;
      }
    }
  }
  return best;
}

/**
 * 카테고리 압축계수. 표본이 부족한 카테고리는 전체 중앙값으로 떨어진다.
 * @param {number|string} categoryId 쿠팡 categoryId (GrowthDB.categoryId 와 같은 축)
 * @returns {{ coef:number, matched:boolean }}
 */
export function fillCoef (categoryId) {
  const v = FILL_COEF.categories[String(categoryId)];
  return v > 0
    ? { coef: v, matched: true }
    : { coef: FILL_COEF.fallback, matched: false };
}

/**
 * 개당 예상 밀크런비.
 * 박스 3종을 모두 계산해 개당이 가장 싼 것을 고른다 (작은 상품은 큰 박스가 유리하다).
 *
 * @param {object} p
 * @param {number} p.sizeX 포장 가로(mm)
 * @param {number} p.sizeY 포장 세로(mm)
 * @param {number} p.sizeZ 포장 높이(mm)
 * @param {number|string} [p.categoryId] 쿠팡 categoryId. 없으면 폴백 계수
 * @returns {null|{ perUnit:number, qty:number, geoQty:number, coef:number,
 *                  coefMatched:boolean, box:string, boxLabel:string, fee:number }}
 *          치수가 없거나 어느 박스에도 안 들어가면 null
 */
export function estimateMilkrunCost ({ sizeX, sizeY, sizeZ, categoryId }) {
  const x = Number(sizeX) || 0;
  const y = Number(sizeY) || 0;
  const z = Number(sizeZ) || 0;
  if (!(x > 0 && y > 0 && z > 0)) return null;

  const { coef, matched } = fillCoef(categoryId);
  let best = null;
  for (const b of BOXES) {
    const geoQty = fitCount(x, y, z, b.sumMm);
    if (!geoQty) continue;
    const qty = Math.max(1, Math.floor(geoQty * coef));
    const perUnit = b.fee / qty;
    if (!best || perUnit < best.perUnit) {
      best = {
        perUnit: Math.round(perUnit),
        qty,
        geoQty,
        coef,
        coefMatched: matched,
        box: b.size,
        boxLabel: b.label,
        fee: b.fee
      };
    }
  }
  return best;
}

export default { BOXES, fitCount, fillCoef, estimateMilkrunCost };
